// Detección de plataforma para atribución de telemetría.
// El panel corre como web (navegador/PWA) y embebido en la app de escritorio (Electron),
// que carga la misma URL HTTPS remota. Sin esta dimensión no es posible distinguir
// sesiones desktop de web en PostHog/Clarity/GA4.
//
// Nota: se detecta vía el bridge `window.electron` (preload), NO vía el User-Agent.
//
// The `getAppVersion` IPC is NOT available on every deployed shell: Sentry issue 7705523549 (born
// 2026-09-01, 35 events) collects shells that reject it with "No handler registered". There
// `getDesktopAppVersion()` degrades to null, so `platform: 'electron'` WITH a null version is the
// signature of an outdated shell, not a broken bridge.

type WindowWithElectron = Window & {
  electron?: {
    ipcRenderer?: {
      invoke: <T>(channel: string, data?: unknown) => Promise<T>;
    };
  };
};

// True cuando el panel corre dentro de la app de escritorio (preload expone window.electron).
export const isElectron = (): boolean =>
  typeof window !== 'undefined' && Boolean((window as WindowWithElectron).electron?.ipcRenderer);

export const getPlatform = (): 'electron' | 'web' => (isElectron() ? 'electron' : 'web');

// Versión de la app de escritorio (vía IPC main `getAppVersion`). null en web o si falla el bridge.
// No confundir con APP_VERSION, que es la versión del build web servido por Firebase Hosting.
export const getDesktopAppVersion = async (): Promise<string | null> => {
  const w = window as WindowWithElectron;
  if (!w.electron?.ipcRenderer) return null;
  try {
    const version = await w.electron.ipcRenderer.invoke<string>('getAppVersion');
    return version || null;
  } catch {
    return null;
  }
};
